import { Box, Typography } from "@mui/material";
import { useMemo } from "react";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import AddFileButton from "../../../../components/button/AddFileButton/AddFileButton";
import AddEditDocsRestrictedDialog from "../../../../components/dialog/AddEditDocsRestrictedDialog/AddEditDocsRestrictedDialog";
import { useDocsTableConfig } from "../../../../components/table/DocsTable/TablesConfig";
import FilterTable from "../../../../components/table/FilterTable";
import LoaderWrapper from "../../../../components/wrapper/LoaderWrapper";
import { useAuth } from "../../../../context/auth";
import { DOCS_RESTRICTED_ROLE, DOCS_RESTRICTED_USER } from "../../../../helpers/constants";
import { useAxiosPrivate } from "../../../../hooks/useAxiosPrivate";
import { useDialogBetter } from "../../../../hooks/useDialogBetter";
import {
    documentsApi,
    useDeleteDocumentFromAgreement,
    useListAgreementDocuments,
} from "../../../../services/documentsService";
import TableService from "../../../../services/tableService";
import { AgreementDraftPreview } from "../../../SalesDraftsPage/DraftPreview/AgreementDraftPreview";
import { AgreementHistoryTable } from "./AgreementHistoryTable";

export const AgreementDocumentsTab = (props) => {
    const { agreement } = props;
    const { t } = useTranslation();

    const { openDialog, closeDialog } = useDialogBetter();
    const { user } = useAuth();
    const axiosPrivate = useAxiosPrivate();

    const { data: documents, isLoading, refetch } = useListAgreementDocuments(agreement.id);
    const { getTableProps, fetchingFromBackend } = useDocsTableConfig();
    const { mutate: deleteDocument } = useDeleteDocumentFromAgreement();

    const onPreview = async (docId) => {
        try {
            await documentsApi.handlePreviewDocumentFromLink(axiosPrivate, docId);
        } catch (error) {
            toast.error(t("snackbar_alert.occurred_error_during_preview"));
        }
    };

    const onDownloadFile = async (docId) => {
        try {
            await documentsApi.handleDownloadDocumentFromLink(axiosPrivate, docId);
        } catch (error) {
            toast.error(t("snackbar_alert.occurred_error_during_download"));
        }
    };

    const onDeleteFile = (docId) => {
        deleteDocument({ document_id: docId, agreement_id: agreement.id });
    };

    const tableProps = useMemo(() => {
        if (fetchingFromBackend) {
            return undefined;
        }
        return getTableProps({ excludedFields: ["manage_products", "docsinve_is_prospectus"] });
    }, [fetchingFromBackend, getTableProps]);

    const preparedDocsData = useMemo(() => {
        if (!tableProps?.tableConfig || !documents) {
            return undefined;
        }

        return TableService.getPreparedDataForCollapsibleTable(
            tableProps.tableConfig,
            documents,
            [],
            [],
            onPreview,
            onDownloadFile,
            onDeleteFile,
            false, // readOnly
            [DOCS_RESTRICTED_ROLE, DOCS_RESTRICTED_USER], // availableDocsRestrictedTypes
            refetch,
            true, // showEditButton
            true, // showRevisionButton
            true, // showStandards
            { agreement_id: agreement.id }, // context
        );
    }, [tableProps?.tableConfig, documents, refetch, agreement.id]);

    return (
        <Box sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={{ display: "flex", flexDirection: "column", height: "45vh" }}>
                <Box
                    sx={{
                        mb: 2,
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                    }}
                >
                    <Typography variant="h6" sx={{ fontWeight: "bold" }}>
                        {t("agreements.documentsTab.title")}
                    </Typography>
                    <AddFileButton
                        variant="contained"
                        color="primary"
                        size={"medium"}
                        disabled={props.readOnly}
                        onClick={() =>
                            openDialog({
                                Component: AddEditDocsRestrictedDialog,
                                componentProps: {
                                    agreement_id: agreement.id,
                                    request_user: user?.username || "",
                                    multiple: true,
                                    showIndustry: true,
                                    availableDocsRestrictedTypes: [
                                        DOCS_RESTRICTED_USER,
                                        DOCS_RESTRICTED_ROLE,
                                    ],
                                    onSubmit: () => {
                                        closeDialog();
                                        refetch();
                                    },
                                },
                            })
                        }
                        fileType={"file"}
                    />
                </Box>
                <Box sx={{ flex: 1, overflow: "auto" }}>
                    <LoaderWrapper showLoader={isLoading || preparedDocsData === undefined}>
                        {tableProps && (
                            <FilterTable
                                // @ts-ignore
                                data={preparedDocsData || []}
                                withPagination={false}
                                showCleanFilterIcon={true}
                                showContextMenu={false}
                                showExportToFileButton={false}
                                headersConfig={TableService.getHeadersConfigForCollapsibleTable(
                                    tableProps.tableConfig,
                                    { loading: false, value: {} },
                                    [],
                                    [],
                                )}
                                showDetailsIcon={false}
                                showCounterRecords={false}
                                showCheckbox={false}
                                showFilters={true}
                                filterPrefix={tableProps.tableFilterPrefix}
                                setSelectedRowId={() => {}}
                                hideFiltersIconCol={true}
                            />
                        )}
                    </LoaderWrapper>
                </Box>
            </Box>
            <Box sx={{ width: "30vw" }}>
                <AgreementDraftPreview agreementId={agreement.id} />
            </Box>
            <Box sx={{ height: "45vh", overflow: "auto" }}>
                <AgreementHistoryTable history={agreement.history} />
            </Box>
        </Box>
    );
};
